import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Check, X, ExternalLink, RefreshCw } from 'lucide-react';
import { useOAuth } from '@/hooks/useOAuth';
import { useOAuthPersistence } from '@/hooks/useOAuthPersistence';

interface YouTubeConnectionProps {
	value: string;
	onChange: (value: string) => void;
	onConnect: (username?: string) => void;
	onDisconnect: () => void;
	onRefresh?: () => void;
	loading?: boolean;
	subscribersCount?: number;
	viewsCount?: number;
}

export default function YouTubeConnection({
	value,
	onChange,
	onConnect,
	onDisconnect,
	onRefresh,
	loading = false,
	subscribersCount = 0,
	viewsCount = 0
}: YouTubeConnectionProps) {
	const { connect, disconnect, isPlatformConfigured } = useOAuth();
	const { isConnected: isPersistedConnected, getConnection } = useOAuthPersistence();
	const [connected, setConnected] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);

	// Vérifier si YouTube est configuré
	const isYouTubeConfigured = isPlatformConfigured('youtube');

	// Charger la connexion persistée au montage
	useEffect(() => {

		// Vérifier aussi directement dans localStorage (YouTube utilise Google OAuth)
		const directCheck = localStorage.getItem('google_user_profile');

		if (isPersistedConnected('youtube') && !connected) {
			const connection = getConnection('youtube');
			if (connection) {
				onConnect(connection.userProfile.username);
			}
		} else if (directCheck && !connected) {
			// Fallback: vérifier directement localStorage (Google data for YouTube)
			try {
				const userProfile = JSON.parse(directCheck);
				onConnect(userProfile.username);
			} catch (error) {
				console.error('Error parsing Google profile from localStorage:', error);
			}
		}
	}, [isPersistedConnected, getConnection, onConnect, connected]);

	// Écouter les événements OAuth
	useEffect(() => {
		const handleOAuthConnected = (event: CustomEvent) => {
			const { platform, userProfile } = event.detail;
			if (platform === 'youtube' && !connected) {
				onConnect(userProfile.username);
			}
		};

		const handleOAuthDisconnected = (event: CustomEvent) => {
			const { platform } = event.detail;
			if (platform === 'youtube' && connected) {
				onDisconnect();
			}
		};

		window.addEventListener('oauth_connected', handleOAuthConnected as EventListener);
		window.addEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);

		return () => {
			window.removeEventListener('oauth_connected', handleOAuthConnected as EventListener);
			window.removeEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);
		};
	}, [onConnect, onDisconnect, connected]);

	// Mettre à jour l'état de connexion
	useEffect(() => {
		setConnected(!!value);
	}, [value]);

	const handleConnect = () => {
		connect('youtube');
	};

	const handleDisconnect = () => {
		// For YouTube, we need to disconnect both youtube and google since YouTube uses Google OAuth
		disconnect('youtube');
		disconnect('google');

		// Also manually clean up Google data since YouTube uses it
		localStorage.removeItem('google_access_token');
		localStorage.removeItem('google_refresh_token');
		localStorage.removeItem('google_expires_at');
		localStorage.removeItem('google_user_profile');
		localStorage.removeItem('google_connected_at');

		// Call the parent disconnect handler
		onDisconnect();
	};

	const handleRefresh = async () => {
		if (onRefresh) {
			setIsRefreshing(true);
			await onRefresh();
			setIsRefreshing(false);
		}
	};

	const connection = getConnection('youtube');
	const displayUsername = connection?.userProfile.displayName || (value ? `@${value.replace('https://www.youtube.com/@', '')}` : "YouTube User");
	const avatarUrl = connection?.userProfile.avatarUrl;
	// Use props if available (they are updated by Settings.tsx refresh), otherwise fallback
	const displaySubscribers = subscribersCount || connection?.userProfile.subscribersCount || 0;
	const displayViews = viewsCount || connection?.userProfile.viewsCount || 0;

	return (
		<div>
			<Label htmlFor="youtube" className="flex items-center gap-2 mb-1">
				<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
					<path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
				</svg>
				YouTube
			</Label>
			{connected ? (
				<div className="space-y-3">
					<div className="flex items-center gap-3 p-3 bg-muted/50 rounded-custom-paddings">
						<button
							onClick={() => {
								const username = connection?.userProfile.username || value?.replace('https://www.youtube.com/@', '');
								window.open(`https://www.youtube.com/@${username}`, '_blank');
							}}
							className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity text-left"
						>
							<Avatar className="w-8 h-8">
								<AvatarImage src={avatarUrl} alt={displayUsername} />
								<AvatarFallback>
									<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
										<path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
									</svg>
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 overflow-hidden">
								<p className="font-medium text-sm truncate">{displayUsername}</p>
								<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
									{loading ? (
										<span className="text-[10px] text-muted-foreground animate-pulse">Loading stats...</span>
									) : (
										<>
											{displaySubscribers > 0 && (
												<div className="text-[10px] text-muted-foreground whitespace-nowrap">
													{displaySubscribers.toLocaleString()} subscribers
												</div>
											)}
											{displayViews > 0 && (
												<div className="text-[10px] text-muted-foreground whitespace-nowrap">
													• {displayViews.toLocaleString()} views
												</div>
											)}
										</>
									)}
								</div>
							</div>
						</button>

						<div className="flex items-center gap-1">
							{onRefresh && (
								<Button
									variant="ghost"
									size="icon"
									onClick={handleRefresh}
									disabled={loading || isRefreshing}
									className="h-8 w-8 text-muted-foreground hover:text-foreground"
									title="Refresh stats"
								>
									<RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
								</Button>
							)}
							<Button
								variant="destructive"
								size="sm"
								onClick={handleDisconnect}
								disabled={loading}
								className="h-8 px-2"
							>
								<X className="w-4 h-4" />
							</Button>
						</div>
					</div>
				</div>
			) : (
				<div className="space-y-2">
					<p className="text-sm text-muted-foreground">
						Connect your YouTube account to automatically retrieve your information
					</p>
					{isYouTubeConfigured ? (
						<Button
							onClick={handleConnect}
							disabled={loading}
							variant="outline"
							className="w-full"
						>
							{/* <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
								<path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
							</svg> */}
							Connect with YouTube
						</Button>
					) : (
						<div className="text-center p-2 bg-muted/50 rounded-custom-paddings">
							<p className="text-xs text-muted-foreground">
								YouTube OAuth is not configured
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
